import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { type ZodType } from "zod";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";

/** The catalogs change rarely and every account reads them. */
export const CATALOG_STALE_TIME_MS = 30 * 60 * 1_000;

type CatalogItemMutationOptions<TItem> = {
	path: string;
	queryKey: QueryKey;
	responseSchema: ZodType<TItem>;
};

/**
 * Posts a new catalog item and appends the server copy to the cached catalog. Callers await it
 * and show the failure next to the form, so it skips the global error modal and never retries.
 */
export const useCreateCatalogItem = <TItem, TRequest extends Record<string, unknown>>({
	path,
	queryKey,
	responseSchema,
}: CatalogItemMutationOptions<TItem>) => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	return useMutation({
		mutationFn: async (request: TRequest) =>
			responseSchema.parse(await post<unknown>(path, request)),
		retry: false,
		onError: logError,
		onSuccess: (created) => {
			queryClient.setQueryData<TItem[]>(queryKey, (items) =>
				items ? [...items, created] : [created],
			);
		},
	});
};
